import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { login, getUser } from '../../modules/auth/_requests';
import { useAuth } from '../../modules/auth/AuthContext';

const initialValues = {
  email: '',
  password: '',
};

const LoginForm = () => {
  const { t } = useTranslation(['auth'], { keyPrefix: 'form' });
  const { t: b } = useTranslation(['auth'], { keyPrefix: 'button' });
  const [viewPassword, setViewPassword] = useState(false);
  const { saveAuth, setCurrentUser } = useAuth();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('email.invalid'))
      .min(3, t('email.invalid'))
      .max(50, t('email.invalid'))
      .required(t('email.required')),
    password: Yup.string()
      .min(8, t('password.min'))
      .max(16, t('password.max'))
      .matches(/[a-zA-Z]/, t('password.alphabetic'))
      .matches(/[0-9]/, t('password.numeric'))
      .required(t('password.required')),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { data: auth } = await login(values.email, values.password);
        saveAuth(auth);
        const { data: user } = await getUser();
        setCurrentUser(user);
      } catch (error: unknown) {
        console.error(error);
        saveAuth(undefined);
        toast.error(t('failed'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Form
      noValidate
      onSubmit={formik.handleSubmit}
      style={{ minWidth: '300px' }}
    >
      <Form.Group controlId="formBasicEmail" className="mb-3">
        <Form.Label>{t('email.label')}</Form.Label>
        <Form.Control
          type="email"
          placeholder={t('email.placeholder')}
          {...formik.getFieldProps('email')}
          isInvalid={formik.touched.email && formik.errors.email !== undefined}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.email}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="formBasicPassword" className="mb-3">
        <Form.Label>{t('password.label')}</Form.Label>
        <Form.Label className="float-end">
          <Link to="/forgot-password">{t('password.lost')}</Link>
        </Form.Label>
        <InputGroup>
          <Form.Control
            type={viewPassword ? 'text' : 'password'}
            placeholder={t('password.placeholder')}
            {...formik.getFieldProps('password')}
            isInvalid={
              formik.touched.password && formik.errors.password !== undefined
            }
          />
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => setViewPassword(!viewPassword)}
            aria-label={t(viewPassword ? 'password.hide' : 'password.show')}
          >
            <i className={`bi bi-${viewPassword ? 'eye' : 'eye-slash'}`}></i>
          </Button>
        </InputGroup>
        <Form.Control.Feedback type="invalid">
          {formik.errors.password}
        </Form.Control.Feedback>
      </Form.Group>
      <div className="d-grid justify-content-end">
        <Button
          variant="outline-dark"
          type="submit"
          className="text-end"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {b(formik.isSubmitting ? 'wait' : 'login')}
        </Button>
      </div>
    </Form>
  );
};

export default LoginForm;
